/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IDobbiesBrandData } from './dobbies-brand.data';
import { IDobbiesBrandProps } from './dobbies-brand.props.autogenerated';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

export interface IDobbiesAlphabeticBrandsData {
    alphabet: string;
    brands: CategoryHierarchy[];
}

interface IDobbiesBrandsState {
    alphabeticallySortedBrands?: IDobbiesAlphabeticBrandsData[];
}
export interface IDobbiesBrandViewProps extends IDobbiesBrandProps<IDobbiesBrandData> {
    alphabeticallySortedBrands?: IDobbiesAlphabeticBrandsData[];
}

/**
 *
 * DobbiesBrand component
 * @extends {React.PureComponent<IDobbiesBrandProps<IDobbiesBrandData>>}
 */
class DobbiesBrand extends React.PureComponent<IDobbiesBrandProps<IDobbiesBrandData>, IDobbiesBrandsState> {
    constructor(props: IDobbiesBrandProps<IDobbiesBrandData>) {
        super(props);
        this.state = {};
    }
    private getAlphabeticallySortedCategories = (): IDobbiesAlphabeticBrandsData[] => {
        const categories = this.props.data.categories.result;
        const alphabeticallySortedBrands: IDobbiesAlphabeticBrandsData[] = [];
        if (categories) {
            const alphabets = [
                'A',
                'B',
                'C',
                'D',
                'E',
                'F',
                'G',
                'H',
                'I',
                'J',
                'K',
                'L',
                'M',
                'N',
                'O',
                'P',
                'Q',
                'R',
                'S',
                'T',
                'U',
                'V',
                'W',
                'X',
                'Y',
                'Z'
            ];
            for (const alphabet of alphabets) {
                const alphabetBrand: IDobbiesAlphabeticBrandsData = { alphabet: alphabet, brands: [] };
                alphabeticallySortedBrands.push(alphabetBrand);
            }
            const seoFriendlyBrandsMenuItemName = this.props.config.seoFriendlyBrandsMenuName || 'Brands';
            let brandsId = -1;
            for (const c of categories) {
                if (c.Name === seoFriendlyBrandsMenuItemName && c.Children && c.Children.length) {
                    brandsId = c.RecordId;
                    for (const bc of c.Children) {
                        this.sortAlphabeticallyCategories(bc, alphabeticallySortedBrands, brandsId);
                    }
                }
            }
        }
        return alphabeticallySortedBrands;
    };

    private getBrandUrlFromConfig = (brandName: string) => {
        const brandDetails = this.props.config.brandsDetails;
        if (brandDetails && brandDetails.length) {
            for (const bd of brandDetails) {
                if (bd.brandName && bd.brandName?.toLowerCase() === brandName.toLowerCase()) {
                    return bd.brandUrl;
                }
            }
        }
        return undefined;
    };

    private sortAlphabeticallyCategories = (
        cat: CategoryHierarchy,
        alphabeticallySortedBrands: IDobbiesAlphabeticBrandsData[],
        brandsId: number
    ) => {
        for (const asb of alphabeticallySortedBrands) {
            if (cat.Name?.startsWith(asb.alphabet.toLowerCase()) || cat.Name?.startsWith(asb.alphabet)) {
                if (cat.ParentCategory === brandsId) {
                    const brandUrlInConfig = this.getBrandUrlFromConfig(cat.Name);
                    if (brandUrlInConfig) {
                        cat.Url = brandUrlInConfig;
                    }
                    asb.brands.push(cat);
                }
                if (cat.Children && cat.Children.length) {
                    for (const cc of cat.Children) {
                        this.sortAlphabeticallyCategories(cc, alphabeticallySortedBrands, brandsId);
                    }
                }
                break;
            }
        }
    };

    public async componentDidMount(): Promise<void> {
        if (this.props.config.showBrandsWithSEOFriendlyUrls) {
            const alphabeticallySortedBrands = this.getAlphabeticallySortedCategories();
            const brandsAddedInConfigOnly = this.props.config.brandsDetails?.filter(b => {
                if (alphabeticallySortedBrands && alphabeticallySortedBrands.length) {
                    for (const abs of alphabeticallySortedBrands) {
                        if (abs.brands && abs.brands.length) {
                            for (const br of abs.brands) {
                                if (br.Name && br.Name?.toLowerCase() === b.brandName?.toLowerCase()) {
                                    return false;
                                }
                            }
                        }
                    }
                }
                return true;
            });

            for (const asb of alphabeticallySortedBrands) {
                if (brandsAddedInConfigOnly && brandsAddedInConfigOnly.length) {
                    for (const configBrand of brandsAddedInConfigOnly) {
                        if (
                            configBrand.brandName?.startsWith(asb.alphabet.toLowerCase()) ||
                            configBrand.brandName?.startsWith(asb.alphabet)
                        ) {
                            const c: CategoryHierarchy = {
                                Name: configBrand.brandName,
                                NeutralizedName: configBrand.brandName,
                                Url: configBrand.brandUrl,
                                Children: [],
                                RecordId: -2
                            };
                            asb.brands.push(c);
                        }
                    }
                }
            }
            this.setState({ alphabeticallySortedBrands: alphabeticallySortedBrands });
        }
    }
    public render(): JSX.Element | null {
        const productCollectionViewProps = {
            ...this.props,
            alphabeticallySortedBrands: this.state.alphabeticallySortedBrands
        };
        return this.props.renderView(productCollectionViewProps);
    }
}

export default DobbiesBrand;
